import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';

import {
  type DragEndEvent,
  type DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  SensorDescriptor,
  SensorOptions,
} from '@dnd-kit/core';
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { isNullish } from 'remeda';

import { Nullable } from 'v2/types';

interface UseSortableListOptions<T> {
  onReorder?: (items: T[]) => void;
}

interface UseSortableList<T> {
  items: T[];
  setItems: Dispatch<SetStateAction<T[]>>;
  itemIds: string[];
  sensors: SensorDescriptor<SensorOptions>[];
  activeId: Nullable<string>;
  activeItem?: Nullable<T>;
  onDragStart: (e: DragStartEvent) => void;
  onDragEnd: (e: DragEndEvent) => void;
  onDragCancel: VoidFunction;
}

export function useSortableList<T extends { id: string }>(
  initialItems: T[],
  options: UseSortableListOptions<T> = {}
): UseSortableList<T> {
  const [items, setItems] = useState<T[]>([]);
  const [activeId, setActiveId] = useState<Nullable<string>>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    setItems(initialItems);
  }, [initialItems]);

  const itemIds = useMemo(() => {
    return items.map((i) => i.id);
  }, [items]);

  const activeItem = useMemo(() => {
    if (isNullish(activeId)) {
      return null;
    }

    return items.find((i) => i.id === activeId);
  }, [activeId, items]);

  const handleDragStart = useCallback((e: DragStartEvent) => {
    setActiveId(e.active.id as string);
  }, []);

  const handleDragEnd = useCallback((e: DragEndEvent) => {
    const { active, over } = e;

    setActiveId(null);

    if (!over || active.id === over.id) return;

    setItems((prev) => {
      const next = arrayMove(
        prev,
        prev.findIndex((i) => i.id === active.id),
        prev.findIndex((i) => i.id === over.id)
      );

      options.onReorder?.(next);

      return next;
    });
  }, []);

  const handleDragCancel = useCallback(() => {
    setActiveId(null);
  }, []);

  return {
    items,
    setItems,
    itemIds,
    sensors,
    activeId,
    activeItem,
    onDragStart: handleDragStart,
    onDragEnd: handleDragEnd,
    onDragCancel: handleDragCancel,
  };
}
